import { useState } from 'react'
import { componenteDidatico, nomeDe } from '../dominio/didatico/catalogo'
import type { PerfilVeiculo } from '../dominio/didatico/perfilVeiculo'
import { FONTES_ANATOMIA, pecaPresenteNoPerfil } from '../dominio/didatico/exploracaoAnatomia'
import './anatomia.css'
import { etapaAnatomiaPorId, ROTEIRO_ANATOMIA, type EtapaAnatomia } from '../dominio/didatico/roteiroAnatomia'
import { IconeConcluido } from './icones'
import { TIPOS_ESTRUTURA_VEICULAR, tipoEstruturaPorId, type TipoEstruturaVeicular } from '../dominio/didatico/tiposEstruturaVeicular'

interface Props {
  perfil: PerfilVeiculo
  selecionada: string | null
  aoInspecionar: (id: string) => void
  etapa: EtapaAnatomia
  concluidas: readonly EtapaAnatomia[]
  aoEscolher: (etapa: EtapaAnatomia) => void
  aoConcluir: (etapa: EtapaAnatomia) => void
  tipoEstrutura: TipoEstruturaVeicular
  aoEscolherTipoEstrutura: (tipo: TipoEstruturaVeicular) => void
  explodido: boolean
  aoMudarExplodido: (explodido: boolean) => void
  /** controle de inflar os airbags; o App só passa com a camada SRS ligada */
  airbags?: {
    /** 0 = fechados, 1 = totalmente inflados */
    expansao: number
    aoMudarExpansao: (expansao: number) => void
    aoDeflagrar: () => void
  }
}

export function PainelAnatomia({
  perfil,
  selecionada,
  aoInspecionar,
  etapa,
  concluidas,
  aoEscolher,
  aoConcluir,
  tipoEstrutura,
  aoEscolherTipoEstrutura,
  explodido,
  aoMudarExplodido,
  airbags,
}: Props) {
  const conteudo = etapaAnatomiaPorId(etapa)
  const estrutura = tipoEstruturaPorId(tipoEstrutura)
  const [busca, setBusca] = useState('')
  const indice = ROTEIRO_ANATOMIA.findIndex(item => item.id === etapa)
  const normalizar = (texto: string) => texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const pecas = conteudo.pecas.filter(id => pecaPresenteNoPerfil(id, perfil))
  const encontradas = pecas.filter(id => normalizar(nomeDe(id)).includes(normalizar(busca.trim())))
  const selecionado = selecionada && pecas.includes(selecionada) ? componenteDidatico(selecionada) : null
  const escolher = (id: EtapaAnatomia) => { setBusca(''); aoEscolher(id) }
  return (
    <div className="grupo-controle roteiro-anatomia">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span className="titulo-controle" style={{ margin: 0 }}>Roteiro de anatomia</span>
        <div className="ribbon-pilulas-cad" role="group" aria-label="Modo de montagem">
          <button
            type="button"
            className={`btn-acao-cad ${!explodido ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
            onClick={() => aoMudarExplodido(false)}
            aria-pressed={!explodido}
            style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
          >
            Montado
          </button>
          <button
            type="button"
            className={`btn-acao-cad ${explodido ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
            onClick={() => aoMudarExplodido(true)}
            aria-pressed={explodido}
            style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
          >
            Explodido
          </button>
        </div>
      </div>
      <div className="progresso-anatomia" aria-label={`${concluidas.length} de ${ROTEIRO_ANATOMIA.length} etapas concluídas`}>
        <i style={{ width: `${(concluidas.length / ROTEIRO_ANATOMIA.length) * 100}%` }} />
      </div>
      <div className="etapas-anatomia">
        {ROTEIRO_ANATOMIA.map((item) => (
          <button key={item.id} className={`${etapa === item.id ? 'ativo' : ''} ${concluidas.includes(item.id) ? 'concluido' : ''}`} aria-pressed={etapa === item.id} onClick={() => escolher(item.id)}>
            {concluidas.includes(item.id) && <IconeConcluido />}{item.nome}
          </button>
        ))}
      </div>
      <div className="conteudo-anatomia">
        <strong>{conteudo.foco}</strong>
        <p>{conteudo.observar}</p>
        <ul className="anatomia-pontos">{conteudo.pontosChave.map(ponto => <li key={ponto}>{ponto}</li>)}</ul>
        <p className="pergunta-didatica"><strong>Pergunta para discussão</strong>{conteudo.pergunta}</p>
        <details key={etapa} className="anatomia-resposta">
          <summary>Revelar explicação</summary>
          <p>{conteudo.explicacao}</p>
        </details>
      </div>
      {airbags && (
        <section className="anatomia-airbags" aria-label="Airbags inflados">
          <span className="titulo-controle">SRS · ver inflado</span>
          <label className="campo">
            <span>Inflar airbags · {Math.round(airbags.expansao * 100)}%</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              aria-label="Inflar airbags"
              value={Math.round(airbags.expansao * 100)}
              onChange={(evento) => airbags.aoMudarExpansao(Number(evento.target.value) / 100)}
            />
          </label>
          {airbags.expansao >= 1
            ? <button type="button" onClick={() => airbags.aoMudarExpansao(0)}>Recolher airbags</button>
            : <button type="button" onClick={airbags.aoDeflagrar}>Deflagrar</button>}
          <p className="dica">Todos os airbags que o perfil declara, ao mesmo tempo, para ver o espaço que ocupam. Numa colisão real só disparam os que o evento aciona, em poucos milissegundos.</p>
        </section>
      )}
      <section className="anatomia-explorador" aria-label="Explorar peças da etapa">
        <label htmlFor="busca-peca-anatomia">Localizar peça nesta etapa · {pecas.length}</label>
        <input id="busca-peca-anatomia" type="search" value={busca} onChange={evento => setBusca(evento.target.value)} placeholder="Nome da peça…" />
        <div className="anatomia-pecas">
          {encontradas.map(id => <button type="button" key={id} aria-pressed={selecionada === id} onClick={() => aoInspecionar(id)}>
            <span>{nomeDe(id)}</span><small>Ver no 3D</small>
          </button>)}
        </div>
        {encontradas.length === 0 && <p role="status">Nenhuma peça encontrada nesta etapa.</p>}
        {selecionado && <article className="anatomia-ficha" aria-live="polite">
          <strong>{selecionado.nome}</strong>
          <p>{selecionado.descricao}</p>
          {selecionado.funcao && <p><b>Função: </b>{selecionado.funcao}</p>}
          {selecionado.atencaoOperacional && <p className="anatomia-atencao"><b>No salvamento: </b>{selecionado.atencaoOperacional}</p>}
          <small>Use Construção para abrir, afastar ou ocultar as peças disponíveis.</small>
        </article>}
      </section>
      {etapa === 'estrutura' && (
        <div className="comparacao-estruturas">
          <span className="titulo-controle">Comparar construção</span>
          <div className="linha-botoes">
            {TIPOS_ESTRUTURA_VEICULAR.map((tipo) => (
              <button key={tipo.id} className={tipoEstrutura === tipo.id ? 'ativo' : ''} onClick={() => aoEscolherTipoEstrutura(tipo.id)}>
                {tipo.nome}
              </button>
            ))}
          </div>
          <dl>
            <div><dt>Construção</dt><dd>{estrutura.construcao}</dd></div>
            <div><dt>No impacto</dt><dd>{estrutura.leituraImpacto}</dd></div>
            <div><dt>Na operação</dt><dd>{estrutura.implicacaoOperacional}</dd></div>
          </dl>
          <p className="dica">O desenho sobre o mesmo veículo compara o princípio estrutural. Não representa um modelo comercial específico.</p>
        </div>
      )}
      <div className="anatomia-navegacao">
        <button type="button" disabled={indice === 0} onClick={() => escolher(ROTEIRO_ANATOMIA[indice - 1]!.id)}>Anterior</button>
        <span>{indice + 1} / {ROTEIRO_ANATOMIA.length}</span>
        <button type="button" disabled={indice === ROTEIRO_ANATOMIA.length - 1} onClick={() => escolher(ROTEIRO_ANATOMIA[indice + 1]!.id)}>Próxima</button>
      </div>
      <button type="button" onClick={() => aoConcluir(etapa)} disabled={concluidas.includes(etapa)}>{concluidas.includes(etapa) ? 'Etapa compreendida' : 'Marcar como compreendida'}</button>
      <details className="anatomia-fontes">
        <summary>Referências e limites da maquete</summary>
        <p>Modelo didático genérico. As formas, posições e materiais ilustram conceitos; não identificam zonas seguras de corte de um veículo real. Consulte a ficha de salvamento e o guia de resposta do modelo e ano, junto ao procedimento operacional adotado.</p>
        <p>Aproxime para observar seções ocas, abas, soldas, nervuras e fixações. Os detalhes são exclusivos da Anatomia e ilustram exemplos construtivos; não reproduzem a seção, a espessura ou o número de chapas de um modelo real.</p>
        <ul>{FONTES_ANATOMIA.map(fonte => <li key={fonte.url}><a href={fonte.url} target="_blank" rel="noreferrer">{fonte.nome}</a></li>)}</ul>
      </details>
      <p className="dica">A posição de reforços, materiais, bateria e dispositivos muda entre modelos. Confirme sempre na ficha de salvamento do veículo.</p>
    </div>
  )
}


